import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Pick the smallest data-width source that still covers the painted box.
 * Used when deferred-media clones an adaptive <video> with data-src sources.
 * @param {HTMLVideoElement} video
 * @returns {HTMLSourceElement[]}
 */
function pickDeferredVideoRenditions(video) {
  const sources = /** @type {HTMLSourceElement[]} */ (
    Array.from(video.querySelectorAll('source[data-src][data-width]'))
  );
  if (sources.length < 2) return sources;

  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const ratio = Math.min(window.devicePixelRatio || 1, coarse ? 1.5 : 2);
  const painted = video.getBoundingClientRect().width || window.innerWidth;
  const target = painted * ratio;
  const ascending = sources
    .slice()
    .sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
  const best = ascending.find((source) => Number(source.dataset.width) >= target);
  return [best || ascending[ascending.length - 1]];
}

/**
 * Copy data-src onto src for adaptive deferred videos, then load().
 * @param {HTMLVideoElement} video
 */
function hydrateDeferredVideo(video) {
  let changed = false;
  const widthSources = video.querySelectorAll('source[data-src][data-width]');

  if (widthSources.length) {
    pickDeferredVideoRenditions(video).forEach((source) => {
      if (!(source instanceof HTMLSourceElement)) return;
      if (source.getAttribute('src')) return;
      source.setAttribute('src', source.dataset.src || '');
      changed = true;
    });
  } else {
    video.querySelectorAll('source[data-src]').forEach((source) => {
      if (!(source instanceof HTMLSourceElement)) return;
      if (source.getAttribute('src')) return;
      source.setAttribute('src', source.dataset.src || '');
      changed = true;
    });
  }

  if (video.dataset.src && !video.getAttribute('src')) {
    video.setAttribute('src', video.dataset.src);
    changed = true;
  }

  if (changed) {
    try {
      video.load();
    } catch {
      /* poster remains */
    }
  }
}

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // The expand button needs the Popover API (top layer escapes the slider's containment).
    if (typeof this.showPopover === 'function') this.setAttribute('data-expandable', '');
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      (event) => {
        // PERF-07 UGC-01: a reveal-on-ready video lets go of its source when another starts,
        // so at most one of them is ever loaded; its cover comes back.
        if (this.hasAttribute('data-reveal-on-ready') && event.target !== this) this.unloadMedia();
        else this.pauseMedia();
      },
      { signal }
    );
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Expands the video to fill the screen (and back). Uses the top layer rather than the
   * Fullscreen API, which iPhones do not support for iframes.
   */
  toggleExpanded = () => {
    const button = this.refs.expandButton;
    if (this.matches(':popover-open')) {
      this.hidePopover();
      return;
    }
    this.loadContent(false);
    this.setAttribute('popover', 'manual');
    this.showPopover();
    if (button instanceof HTMLElement) {
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', 'Exit full screen');
    }
    this.addEventListener('toggle', this.#onExpandedToggle);
    document.addEventListener('keydown', this.#onExpandedKeydown);
  };

  /** @param {Event} event */
  #onExpandedToggle = (event) => {
    if (/** @type {ToggleEvent} */ (event).newState !== 'closed') return;
    this.removeAttribute('popover');
    this.removeEventListener('toggle', this.#onExpandedToggle);
    document.removeEventListener('keydown', this.#onExpandedKeydown);
    const button = this.refs.expandButton;
    if (button instanceof HTMLElement) {
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', 'Full screen');
    }
  };

  /** @param {KeyboardEvent} event */
  #onExpandedKeydown = (event) => {
    if (event.key === 'Escape' && this.matches(':popover-open')) this.hidePopover();
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement) {
      if (this.hasAttribute('data-reveal-on-ready')) this.revealWhenReady(content);
      hydrateDeferredVideo(content);
      if (content.getAttribute('autoplay')) {
        // force autoplay for safari
        content.play()?.catch?.(() => {});
      }
    }
  }

  /**
   * PERF-07 UGC-01: the cover stays up, its play button pulsing (CSS), until the video can
   * play; then the video fades in over it. No spinner, black or half-loaded frame.
   * @param {HTMLVideoElement} video
   */
  revealWhenReady(video) {
    const ready = () => {
      video.removeEventListener('error', failed);
      this.setAttribute('data-media-ready', '');
    };
    const failed = () => {
      video.removeEventListener('canplay', ready);
      this.unloadMedia();
    };
    video.addEventListener('canplay', ready, { once: true });
    video.addEventListener('error', failed, { once: true });
  }

  /** Back to the cover: the cloned video and its source are released. */
  unloadMedia() {
    if (!this.getAttribute('data-media-loaded')) return;
    const video = this.querySelector(':scope > video');
    if (video) {
      video.pause();
      for (const source of video.querySelectorAll('source[src]')) source.removeAttribute('src');
      video.removeAttribute('src');
      video.load();
      video.remove();
    }
    this.removeAttribute('data-media-loaded');
    this.removeAttribute('data-media-ready');
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
    this.isPlaying = false;
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
